import { useContext, useState, useEffect } from 'react';
import Actualizar from '../views/ActualizarProfileView/Actualizar';
import Kyc from '../components/Profile/Kyc';
import CuentasBancarias from './ActualizarProfileView/CuentasBancarias';
import AuthContext from '../context/autenticacion/authContext';
import styled, { keyframes } from 'styled-components';
import { useT } from 'i18n';

// Animaciones
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components (solo para elementos internos, no para el contenedor principal)
const TabBar = styled.div`
  display: flex;
  gap: 0.5rem;
  background: rgba(12,14,19, 0.4);
  border-radius: 60px;
  padding: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const TabItem = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.active 
    ? 'rgba(227,168,43,.14)'
    : 'transparent'};
  color: ${props => props.active ? '#e3a82b' : 'rgba(255, 255, 255, 0.7)'};
  
  &:hover {
    transform: translateY(-2px);
    color: white;
    background: ${props => props.active 
      ? 'rgba(227,168,43,.14)'
      : 'rgba(227,168,43, 0.2)'};
  }
  
  i {
    margin-right: 0.5rem;
  }
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #0c0e13, #0c0e13);
  border-radius: 24px;
  border: 1px solid rgba(227,168,43, 0.2);
  overflow: hidden;
  height: 100%;
`;

const InfoCardHeader = styled.div`
  background: linear-gradient(135deg, rgba(18,21,28, 0.8), rgba(12,14,19, 0.9));
  padding: 1.5rem;
  text-align: center;
  border-bottom: 1px solid rgba(227,168,43, 0.2);
  
  i {
    font-size: 3rem;
    color: #e3a82b;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
  }
`;

const InfoCardBody = styled.div`
  padding: 1.5rem;
  
  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin-bottom: 1rem;
  }
`;

const ContentWrapper = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
`;

const UserProfile = () => {
  const { t } = useT();
  const authContext = useContext(AuthContext);
  const {
    status,
    userStatus,
    usuario,
    listDatos,
    cuentas_internacionales,
    cuentas_nacionales,
    cuentas_remesas,
    ListaNacionales,
    ListaInternacionales,
    ListaRemesas
  } = authContext;

  const [mensajeDos, guardarMensajeDos] = useState('');
  const [apiload, guardarApiLoad] = useState(true);
  const [recargardata, setRecargarData] = useState(false);
  const [cuentaspropias, setCuentasPropias] = useState([]);
  const [cuentasterceros, setCuentasTerceros] = useState([]);
  const [cuentasremesas, setCuentasRemesas] = useState([]);

  const [appbar, guardarAppBar] = useState({ primero: 1, segundo: 0, tercero: 0 });
  const [appbarkyc, guardarAppBarKyc] = useState({ primero: 1, segundo: 0, tercero: 0 });

  const ApiLoad = () => {
    guardarApiLoad(false);
    setTimeout(() => guardarApiLoad(true), 3);
  };

  // Funciones de navegación
  const ActivarPrimero = () => guardarAppBar({ ...appbar, primero: 1, segundo: 0, tercero: 0 });
  const ActivarSegundo = () => guardarAppBar({ ...appbar, primero: 0, segundo: 1, tercero: 0 });
  const ActivarPrimeroKyc = () => guardarAppBarKyc({ ...appbarkyc, primero: 1, segundo: 0, tercero: 0 });
  const ActivarSegundoKyc = () => guardarAppBarKyc({ ...appbarkyc, primero: 0, segundo: 1, tercero: 0 });
  const ActivarTerceroKyc = () => guardarAppBarKyc({ ...appbarkyc, primero: 0, segundo: 0, tercero: 1 });

  useEffect(() => {
    setCuentasTerceros(cuentas_internacionales);
    setCuentasPropias(cuentas_nacionales);
    setCuentasRemesas(cuentas_remesas);
  }, [apiload, cuentas_internacionales, cuentas_nacionales, cuentas_remesas]);

  // Card informativa KYC
  const KycInfoCard = () => (
    <InfoCard>
      <InfoCardHeader>
        <i className="tim-icons icon-lock-circle" />
        <h3>{t('kyc.queEsTitulo')}</h3>
      </InfoCardHeader>
      <InfoCardBody>
        <p>{t('kyc.queEsP1')}</p>
        <p>{t('kyc.queEsP2')}</p>
      </InfoCardBody>
    </InfoCard>
  );

  // Tabs para usuario con KYC pendiente (status 1 o 2)
  const renderKycTabs = () => (
    <TabBar>
      <TabItem active={appbarkyc.primero === 1} onClick={ActivarPrimeroKyc}>
        <i className="tim-icons icon-single-02" /> {t('cuenta.tabDatos')}
      </TabItem>
      <TabItem active={appbarkyc.segundo === 1} onClick={ActivarSegundoKyc}>
        <i className="tim-icons icon-shield" /> {t('cuenta.tabVerificacion')}
      </TabItem>
    </TabBar>
  );

  return (
    <div className="content">
      {/* Tabs según estado del usuario */}
      {(status === 1 || status === 2) && renderKycTabs()}

      {/* Contenido para usuario con KYC pendiente (status 1 o 2) */}
      {(status === 1 || status === 2) && (
        <>
          {appbarkyc.primero === 1 && (
            <ContentWrapper>
              <Actualizar />
            </ContentWrapper>
          )}

          {appbarkyc.segundo === 1 && (
            <ContentWrapper>
              <div className="row">
                <div className="col-md-8">
                  <Kyc
                    recargardata={recargardata}
                    setRecargarData={setRecargarData}
                    userStatus={userStatus}
                    guardarAppBar={guardarAppBar}
                    guardarAppBarKyc={guardarAppBarKyc}
                    appbar={appbar}
                    appbarkyc={appbarkyc}
                  />
                </div>
                <div className="col-md-4">
                  <KycInfoCard />
                </div>
              </div>
            </ContentWrapper>
          )}

          {appbarkyc.tercero === 1 && (
            <ContentWrapper>
              <CuentasBancarias />
            </ContentWrapper>
          )}
        </>
      )}

      {/* Contenido para usuario con KYC completo (status 3) */}
      {status === 3 && (
        <>
          {appbar.primero === 1 && (
            <ContentWrapper>
              <Actualizar />
            </ContentWrapper>
          )}

          {appbar.segundo === 1 && (
            <ContentWrapper>
              <CuentasBancarias />
            </ContentWrapper>
          )}
        </>
      )}
    </div>
  );
};

export default UserProfile;